// @givmo/employer-module — where a signed-in caller with NO company lands (EP-SUITE2-FIX5).
//
// A Firebase session without an admin company has no portal: every `/admin/company*` read answers
// 403 "Permission denied", so `/dashboard` is a wall of error cards whose "Try again" keeps failing.
// Such a caller is part-way through the sign-up funnel — a provider sign-up that has not registered
// its company yet, a Microsoft account that still owes the OTP, an email sign-up that stopped at its
// code or whose registration after the code was refused (NF-FIX21: the email path registers only after
// the code, so this is its ordinary half-way state), an email requester waiting on an Owner — and the
// funnel already has a screen for each. ONE pure decision picks it from the Firebase user alone, and
// every door that meets such a caller uses it: the portal layouts (`/` and every portal route, on
// their company read's 403), the public doors' arrival guard (`/login-home`, `/sign-in`, `/sign-up`,
// `/sign-up/email-password`) and a sign-in that found no company. No new UI: each landing is an
// existing funnel screen.
//
//   not yet verified, OTP-repairable (the sign-in's Microsoft-OTP-first rule, `needsBackendOtpVerification`,
//   which since NF-FIX24 also covers an APP account: both apps link a phone at sign-up and never verify
//   the email; or an address moved in an app since, NF-FIX19):
//     a microsoft.com or app account   → /sign-up/otp?provider=true (post-confirm re-resolves the route)
//     an email account, join-or-create → /sign-up/otp?join=true (post-confirm lands on request-access)
//     an email account, switch OFF     → /sign-up/otp?provider=true (post-confirm re-resolves: no company
//                                        → the details screen, which registers it) — NF-FIX21
//   not yet verified, and no OTP-repair rule covers it (an account with no email on its sign-in
//   record, or one whose address an attesting provider carries while its flag is still clear)
//                                      → /sign-up/otp?provider=true, whatever the switch: the company create
//                                        refuses an unverified email (NF-FIX21), and the details screen
//                                        cannot offer a code, so the code comes first; post-confirm
//                                        re-resolves (no company → the details screen)
//   verified, with a federated provider (Google; a Microsoft account after its OTP)
//                                      → /sign-up/details?provider=true — the provider sign-in's own
//                                        `unregistered` landing, whose submit resolves the domain first,
//                                        so a joinable one still pivots to request-access
//   a verified email account, join-or-create on
//                                      → /sign-up/request-access, whose re-resolve (EP-SUITE2-FIX3) names
//                                        the company, lets an approved member into the portal, or hands a
//                                        domain with nothing to join to the details screen
//   a verified email account, switch OFF
//                                      → /sign-up/details?provider=true, which registers its company — NF-FIX21
//   a record with no sign-in method    → null — the door keeps what it did before — unless the account
//                                        is an EMPLOYEE of a company on Givmo (a company SSO sign-in's
//                                        account has no sign-in method of its own): then
//                                        /sign-up/request-access, which says how to get in (D-EP-60, 5;
//                                        `landingForEmployee`, below, over `employer-of-record.ts`).
//
// D-EP-60 (5), the rule over the whole table: a caller whose company is already on Givmo is never
// offered to register one. The details screen — the one register offer a signed-in caller can reach —
// asks first and hands such a caller to the request-access screen, so every row above that lands
// there stays as it is.
//
// Until NF-FIX21 an email account with join-or-create OFF (prod's state) had no landing: the email
// sign-up registered its company before the code and rolled the account back when that failed, so such
// an account could only be a stray. Now it is the sign-up's ordinary half-way state, and leaving it on
// "Could not load your company" would strand every person who closed the tab at the code screen.
//
// ⚠ LOOP SAFETY. `/sign-up` lands such a caller, so no landing screen may send a signed-in,
// company-less caller back to `/sign-up`: the request-access screen's fallbacks for a verified caller
// end on the details screen, the one landing with no automatic exit (pinned in
// `sign-up-join-or-create.test.tsx`).
//
// ONE DECISION, TWO HOSTS (EP-MNT8). The table is the funnel's law, so the company console asks the
// same function with its own address book (`CONSOLE_SIGN_UP_PATHS`): its layout lands a company-less
// caller there once the class-mismatch forward has had its say (`apps/company`
// `components/require-company-admin.tsx`). The reads below default to the portal's book.

import type { QueryClient } from '@tanstack/react-query';
import type { User } from 'firebase/auth';
import { isGivmoError } from '@givmo/api-client';
import { companiesGetCompanyOptions } from '@givmo/api-client/query';
import { needsBackendOtpVerification, NoCompanyError } from '@givmo/auth';
import { isCompanyJoinOrCreateEnabled } from '../../lib/env';
import { readEmployerOfRecord, type EmployerOfRecord } from './employer-of-record';
import { PORTAL_SIGN_UP_PATHS, type SignUpPaths } from './auth-routing';

/**
 * A landing, as `navigate()` and a `beforeLoad`'s `redirect()` both take it — in the address book it
 * was decided over (the portal's literal paths by default, so its typed router accepts it as is).
 */
export type LandingTarget<P extends SignUpPaths = typeof PORTAL_SIGN_UP_PATHS> =
  | { to: P['dashboard'] }
  | { to: P['signUpOtp']; search: { provider: true } | { join: true } }
  | { to: P['signUpDetails']; search: { provider: true } }
  | { to: P['signUpRequestAccess'] };

/** The account's sign-in methods (`password`, `google.com`, `microsoft.com`, …). */
function providerIdsOf(user: User): string[] {
  return user.providerData.map((provider) => provider.providerId);
}

/**
 * Where a signed-in caller with no company lands — the header's table. Pure: the Firebase user, the
 * join-or-create switch and the host's address book, nothing else. `null` when no funnel screen fits
 * (the door stays as it was).
 */
export function companylessLanding<P extends SignUpPaths = typeof PORTAL_SIGN_UP_PATHS>(
  user: User,
  joinOrCreateEnabled: boolean,
  paths: P = PORTAL_SIGN_UP_PATHS as unknown as P,
): LandingTarget<P> | null {
  const providerIds = providerIdsOf(user);
  if (providerIds.length === 0) return null;
  const emailOnly = providerIds.every((id) => id === 'password');
  if (needsBackendOtpVerification(user)) {
    if (!emailOnly) return { to: paths.signUpOtp, search: { provider: true } };
    return joinOrCreateEnabled
      ? { to: paths.signUpOtp, search: { join: true } }
      : { to: paths.signUpOtp, search: { provider: true } };
  }
  // NF-FIX21: an unverified account no OTP-repair rule covers (a phone, or an attesting provider whose
  // address moved since) proves its email before the details screen, whose create refuses it.
  if (user.emailVerified !== true) return { to: paths.signUpOtp, search: { provider: true } };
  if (!emailOnly) return { to: paths.signUpDetails, search: { provider: true } };
  return joinOrCreateEnabled
    ? { to: paths.signUpRequestAccess }
    : { to: paths.signUpDetails, search: { provider: true } };
}

/**
 * D-EP-60 (5): where a company-less caller the table cannot place lands once its company is known — a
 * company SSO sign-in's account has no sign-in method of its own, so the table answers `null`, yet it
 * is an employee of a company on Givmo, and the request-access screen tells it how to get in. Any
 * landing the table did name is kept.
 */
export function landingForEmployee<P extends SignUpPaths>(
  landing: LandingTarget<P> | null,
  employer: EmployerOfRecord | null,
  paths: P,
): LandingTarget<P> | null {
  if (landing !== null || employer === null) return landing;
  return { to: paths.signUpRequestAccess };
}

/**
 * The company-less signal: `GET /admin/company` answers 403 exactly when the caller administers no
 * company (no Givmo `user` row, or an empty `admin_company` — backend `companies.py` `get_company`).
 * An App Check or token failure answers 401, and a transport failure has no status, so neither is
 * ever read as "no company".
 */
export function isCompanylessRead(error: unknown): boolean {
  return isGivmoError(error) && error.status === 403;
}

/**
 * Where a signed-in visitor at a door lands: into the portal when their company read succeeds, on
 * their funnel screen when it answers the company-less 403, and nowhere (`null`: stay) on any other
 * failure. The read is the app's own company query, asked ONCE (`retry: false`) through the cache the
 * portal reads, so a portal landing starts warm.
 */
export async function landSignedInVisitor(
  queryClient: QueryClient,
  user: User,
): Promise<LandingTarget | null> {
  try {
    await queryClient.ensureQueryData({ ...companiesGetCompanyOptions(), retry: false });
  } catch (error) {
    if (!isCompanylessRead(error)) return null;
    const landing = companylessLanding(user, isCompanyJoinOrCreateEnabled());
    if (landing !== null) return landing;
    // D-EP-60 (5): the account the table cannot place may be an employee of a company on Givmo.
    return landingForEmployee(
      landing,
      await readEmployerOfRecord(queryClient, user),
      PORTAL_SIGN_UP_PATHS,
    );
  }
  return { to: PORTAL_SIGN_UP_PATHS.dashboard };
}

/**
 * A sign-in whose credential was accepted but whose tail found no company (`NoCompanyError`): the
 * account IS signed in, so land it rather than fail a sign-in that succeeded. `null` for any other
 * failure, and when the re-read cannot place the account — the caller then surfaces the error,
 * exactly as before (the tail cannot tell "no company" from "the read failed"; this re-read can).
 */
export async function landSignInWithoutCompany(
  thrown: unknown,
  queryClient: QueryClient,
): Promise<LandingTarget | null> {
  const credential = NoCompanyError.credentialOf(thrown);
  if (credential === null) return null;
  return landSignedInVisitor(queryClient, credential.user);
}
